import { iconNavDots, iconNavLixeira } from '@bsds-lib-component/icons/icons';
import { BsdsCard, BsdsCheckbox, BsdsIcon } from '@bsds-lib-component/react';
import React, { useEffect, useRef, useState } from 'react';

import type { InclusionProponenteTableItem, InclusionProponentesTableProps } from '../../../types/vida/table/inclusionProponentesTableType';
import Button from '../../button/Button';
import Pagination from '../../pagination/Pagination';
import Tooltip from '../../tooltip/Tooltip';
import TableMenuPortal from '../shared/TableMenuPortal';
import { TABLE_MENU_ITEM_BUTTON_CLASS } from '../shared/tableMenuUtils';

export const PROPONENTES_SORT_OPTIONS = [
  { label: 'Status (A-Z)', value: 'status' },
  { label: 'Status (Z-A)', value: 'status-desc' },
  { label: 'CPF (A-Z)', value: 'cpf' },
  { label: 'CPF (Z-A)', value: 'cpf-desc' },
  { label: 'Nome (A-Z)', value: 'nome' },
  { label: 'Nome (Z-A)', value: 'nome-desc' },
  { label: 'Sexo (A-Z)', value: 'sexo' },
  { label: 'Sexo (Z-A)', value: 'sexo-desc' },
  { label: 'Nascimento (A-Z)', value: 'nascimento' },
  { label: 'Nascimento (Z-A)', value: 'nascimento-desc' },
  { label: 'Matrícula (A-Z)', value: 'matricula' },
  { label: 'Matrícula (Z-A)', value: 'matricula-desc' },
  { label: 'Cargo/Funcao (A-Z)', value: 'cargoFuncao' },
  { label: 'Cargo/Funcao (Z-A)', value: 'cargoFuncao-desc' },
];

const getRowSelectionId = (identProponente: number) => String(identProponente);

const InclusionProponentesTable: React.FC<InclusionProponentesTableProps> = ({
  rows,
  currentPage,
  totalPages,
  totalItems,
  onPageChange,
  onDeleteSelected,
  onRowClick,
  emptyMessage = 'Nenhum proponente encontrado para a pesquisa informada',
  isDeleting = false,
}) => {
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [openItemMenuId, setOpenItemMenuId] = useState<number | null>(null);
  const itemMenuRefs = useRef<Map<number, HTMLDivElement>>(new Map());
  const itemMenuButtonRefs = useRef<Map<number, HTMLButtonElement>>(new Map());
  const itemEditButtonRefs = useRef<Map<number, HTMLButtonElement>>(new Map());

  useEffect(() => {
    setSelectedIds(new Set());
  }, [rows]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (openItemMenuId !== null) {
        const itemMenuRef = itemMenuRefs.current.get(openItemMenuId);
        const itemMenuButtonRef = itemMenuButtonRefs.current.get(openItemMenuId);
        const target = event.target as Node;
        if (
          itemMenuRef
          && !itemMenuRef.contains(target)
          && !itemMenuButtonRef?.contains(target)
        ) {
          setOpenItemMenuId(null);
        }
      }
    };

    const handleEscape = (event: globalThis.KeyboardEvent) => {
      if (event.key === 'Escape' && openItemMenuId !== null) {
        const itemMenuButtonRef = itemMenuButtonRefs.current.get(openItemMenuId);
        setOpenItemMenuId(null);
        itemMenuButtonRef?.focus();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape as unknown as EventListener);
    };
  }, [openItemMenuId]);

  useEffect(() => {
    if (openItemMenuId !== null) {
      itemEditButtonRefs.current.get(openItemMenuId)?.focus();
    }
  }, [openItemMenuId]);

  const allSelected = rows.length > 0 && rows.every((row) => selectedIds.has(getRowSelectionId(row.identProponente)));
  const hasSelectedItems = selectedIds.size > 0;

  const handleSelectAll = () => {
    if (allSelected) {
      setSelectedIds(new Set());
      return;
    }
    setSelectedIds(new Set(rows.map((row) => getRowSelectionId(row.identProponente))));
  };

  const handleSelectItem = (identProponente: number) => {
    const rowId = getRowSelectionId(identProponente);
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(rowId)) {
        next.delete(rowId);
      } else {
        next.add(rowId);
      }
      return next;
    });
  };

  const handleDelete = () => {
    if (!hasSelectedItems || isDeleting) {
      return;
    }

    const selectedRows = rows.filter((row) => selectedIds.has(getRowSelectionId(row.identProponente)));
    onDeleteSelected?.(selectedRows);
  };

  const handleItemMenuToggle = (itemId: number, event: React.MouseEvent) => {
    event.stopPropagation();
    setOpenItemMenuId(openItemMenuId === itemId ? null : itemId);
  };

  const handleItemMenuKeyDown = (itemId: number, event: React.KeyboardEvent) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      event.stopPropagation();
      setOpenItemMenuId(openItemMenuId === itemId ? null : itemId);
    } else if (event.key === 'ArrowDown') {
      event.preventDefault();
      event.stopPropagation();
      if (openItemMenuId !== itemId) {
        setOpenItemMenuId(itemId);
      } else {
        itemEditButtonRefs.current.get(itemId)?.focus();
      }
    } else if (event.key === 'Escape' && openItemMenuId === itemId) {
      event.preventDefault();
      event.stopPropagation();
      setOpenItemMenuId(null);
      itemMenuButtonRefs.current.get(itemId)?.focus();
    }
  };

  const handleEdit = (row: InclusionProponenteTableItem, event: React.MouseEvent) => {
    event.stopPropagation();
    onRowClick?.(row);
    setOpenItemMenuId(null);
  };

  const handleEditKeyDown = (row: InclusionProponenteTableItem, event: React.KeyboardEvent) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      event.stopPropagation();
      onRowClick?.(row);
      setOpenItemMenuId(null);
    } else if (event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      setOpenItemMenuId(null);
      itemMenuButtonRefs.current.get(row.identProponente)?.focus();
    }
  };

  return (
    <BsdsCard className="bsds-p-16">
      <div className="bsds-display-flex bsds-justify-content-between bsds-align-items-center bsds-mb-16 gap-4">
        <div className="bsds-display-flex bsds-align-items-center">
          <h6 className="opacity-80">Proponentes</h6>
        </div>
        {(hasSelectedItems || isDeleting) && (
          <Button
            type="button"
            color="secondary"
            fill="outline"
            icon={iconNavLixeira}
            iconPosition="start"
            onClick={handleDelete}
            disabled={isDeleting}
            loading={isDeleting}
            aria-label={`Excluir ${selectedIds.size} proponente(s) selecionado(s)`}
          >
            Excluir
          </Button>
        )}
      </div>

      <div className="w-full overflow-x-auto custom-scrollbar rounded-md shadow-xs">
        <table className="min-w-[1100px] w-full table-fixed" aria-label="Tabela de proponentes da inclusao">
          <thead>
            <tr>
              {onRowClick && (
                <th className="px-4 py-2 text-left text-sm font-semibold border-b border-gray-100 whitespace-nowrap w-16" scope="col">
                  <Tooltip label="Ações">
                    <span>Ações</span>
                  </Tooltip>
                </th>
              )}
              <th className="px-2 py-2 text-left text-sm font-semibold border-b border-gray-100 whitespace-nowrap w-16" scope="col">
                <Tooltip label="Selecionar todos os proponentes da pagina">
                  <BsdsCheckbox
                    checked={allSelected}
                    onBsdsChange={handleSelectAll}
                    size="small"
                    color="secondary"
                    aria-label="Selecionar todos os proponentes da pagina"
                  />
                </Tooltip>
              </th>
              <th className="px-4 py-2 text-left text-sm font-semibold border-b border-gray-100 whitespace-nowrap w-34" scope="col">Status</th>
              <th className="px-4 py-2 text-left text-sm font-semibold border-b border-gray-100 whitespace-nowrap w-28" scope="col">CPF</th>
              <th className="px-6 py-2 text-left text-sm font-semibold border-b border-gray-100 whitespace-nowrap w-88" scope="col">Nome</th>
              <th className="px-4 py-2 text-left text-sm font-semibold border-b border-gray-100 whitespace-nowrap" scope="col">Sexo</th>
              <th className="px-4 py-2 text-left text-sm font-semibold border-b border-gray-100 whitespace-nowrap" scope="col">Nascimento</th>
              <th className="px-4 py-2 text-left text-sm font-semibold border-b border-gray-100 whitespace-nowrap" scope="col">Matrícula</th>
              <th className="px-4 py-2 text-left text-sm font-semibold border-b border-gray-100 whitespace-nowrap" scope="col">Cargo/Função</th>
            </tr>
          </thead>

          <tbody className="bg-white divide-y divide-gray-50 text-text-color">
            {rows.length > 0 ? (
              rows.map((row) => {
                const isSelected = selectedIds.has(getRowSelectionId(row.identProponente));

                return (
                  <tr
                    key={`${row.identProponente}-${row.matricula}`}
                    className="hover:bg-gray-50 transition-colors text-xs"
                    aria-selected={isSelected}
                  >
                    {onRowClick && (
                      <td className="px-4 py-2 whitespace-nowrap" onClick={(event) => event.stopPropagation()}>
                        <Tooltip label={`Ações do proponente ${row.nome}`}>
                          <button
                            type="button"
                            ref={(el) => {
                              if (el) {
                                itemMenuButtonRefs.current.set(row.identProponente, el);
                              } else {
                                itemMenuButtonRefs.current.delete(row.identProponente);
                              }
                            }}
                            onClick={(event) => handleItemMenuToggle(row.identProponente, event)}
                            onKeyDown={(event) => handleItemMenuKeyDown(row.identProponente, event)}
                            className="hover:bg-gray-100 focus-visible:outline focus-visible:outline-offset-2 focus-visible:outline-gray-500 rounded transition-colors p-1"
                            aria-label={`Ações do proponente ${row.nome}`}
                            aria-expanded={openItemMenuId === row.identProponente}
                            aria-haspopup="menu"
                            aria-controls={`proponente-menu-${row.identProponente}`}
                            aria-pressed={openItemMenuId === row.identProponente}
                          >
                            <BsdsIcon icon={iconNavDots} className="w-3 h-3 text-gray-600" aria-hidden="true" />
                          </button>
                        </Tooltip>
                        <TableMenuPortal
                          isOpen={openItemMenuId === row.identProponente}
                          anchorElement={itemMenuButtonRefs.current.get(row.identProponente) ?? null}
                          menuRef={(el) => {
                            if (el) {
                              itemMenuRefs.current.set(row.identProponente, el);
                            } else {
                              itemMenuRefs.current.delete(row.identProponente);
                            }
                          }}
                          align="left"
                          id={`proponente-menu-${row.identProponente}`}
                          className="w-40"
                          aria-label={`Menu de ações do proponente ${row.nome}`}
                        >
                          <button
                            type="button"
                            ref={(el) => {
                              if (el) {
                                itemEditButtonRefs.current.set(row.identProponente, el);
                              } else {
                                itemEditButtonRefs.current.delete(row.identProponente);
                              }
                            }}
                            onClick={(event) => handleEdit(row, event)}
                            onKeyDown={(event) => handleEditKeyDown(row, event)}
                            role="menuitem"
                            className={TABLE_MENU_ITEM_BUTTON_CLASS}
                          >
                            Editar
                          </button>
                        </TableMenuPortal>
                      </td>
                    )}
                    <td className="px-2 py-2 whitespace-nowrap text-left" onClick={(event) => event.stopPropagation()}>
                      <Tooltip label={`Selecionar proponente ${row.nome}`}>
                        <BsdsCheckbox
                          checked={isSelected}
                          onBsdsChange={() => handleSelectItem(row.identProponente)}
                          size="small"
                          color="secondary"
                          aria-label={`Selecionar proponente ${row.nome}`}
                        />
                      </Tooltip>
                    </td>
                    <td className="px-4 py-2 whitespace-nowrap">{row.status}</td>
                    <td className="px-4 py-2 whitespace-nowrap">{row.cpf}</td>
                    <td className="px-6 py-2 whitespace-nowrap">
                      <Tooltip label={row.nome}>
                        <span>{row.nome}</span>
                      </Tooltip>
                    </td>
                    <td className="px-4 py-2 whitespace-nowrap">{row.sexo}</td>
                    <td className="px-4 py-2 whitespace-nowrap">{row.nascimento}</td>
                    <td className="px-4 py-2 whitespace-nowrap">{row.matricula}</td>
                    <td className="px-4 py-2 whitespace-nowrap">{row.cargoFuncao}</td>
                  </tr>
                );
              })
            ) : (
              <tr>
                <td colSpan={onRowClick ? 9 : 8} className="px-4 py-4 text-center text-text-color text-xs" role="status" aria-live="polite">
                  {emptyMessage}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {totalItems > 0 && (
        <div className="bsds-mt-16 bsds-mb-16">
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={onPageChange}
            totalItems={totalItems}
            itemLabel="Proponentes"
            alwaysShow
          />
        </div>
      )}
    </BsdsCard>
  );
};

export default InclusionProponentesTable;
